import { Link } from 'react-router-dom';
import { SEOHead, BreadcrumbNav } from '@/components/feature/SEO';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';

const featuredArticles = articles.slice(0, 4);
const recentArticles = articles.slice(4, 8);

export default function Home() {
  const breadcrumbs = [
    { name: '首页', url: '/' },
  ];

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '制服诱惑',
    url: 'https://mxnloms.cn',
    description: '探索制服诱惑的时尚魅力与文化内涵，涵盖护士、空姐、警察、学生等经典制服风格，提供专业制服造型搭配、角色扮演服装指南与影视制服文化解析。',
    potentialAction: {
      '@type': 'SearchAction',
      'target': 'https://mxnloms.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <main>
      <SEOHead
        title="首页"
        description="探索制服诱惑的时尚魅力与文化内涵，涵盖护士、空姐、警察、学生等经典制服风格，提供专业制服造型搭配、角色扮演服装指南与影视制服文化解析。"
        keywords="制服诱惑,制服造型搭配,角色扮演服装,职业制服风格,制服cosplay"
        canonicalUrl="https://mxnloms.cn"
        schema={{ ...websiteSchema, ...faqSchema }}
        breadcrumbs={breadcrumbs}
      />

      <BreadcrumbNav items={breadcrumbs} />

      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20editorial%20with%20diverse%20professional%20uniforms%20including%20nurse%20flight%20attendant%20and%20student%20styles%2C%20warm%20golden%20lighting%2C%20clean%20cream%20backdrop%2C%20artistic%20composition%2C%20high%20fashion%20photography%20with%20soft%20shadows%2C%20luxurious%20fabric%20textures&width=1600&height=900&seq=hero-home-01&orientation=landscape"
          alt="制服诱惑 - 职业造型的魅力密码"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-6">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 tracking-tight leading-tight">
            职业造型的魅力密码
          </h1>
          <p className="text-sm md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
            从白衣天使到蓝天之上，从校园青春到职场精英——探索制服诱惑的时尚魅力与文化内涵，发现属于你的独特造型灵感
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/category/nurse"
              className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              探索制服风格
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium hover:bg-white/30 transition-colors whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              探索制服风格
            </h2>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              八大经典制服分类，每一种都蕴含着独特的美学密码
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative overflow-hidden rounded-lg bg-background-100 hover:bg-background-200 transition-colors p-5 md:p-6 flex flex-col items-center text-center cursor-pointer"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-200 transition-colors mb-4">
                  <i className={`${cat.icon} text-xl md:text-2xl text-accent-600`}></i>
                </div>
                <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 whitespace-nowrap">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 line-clamp-2">
                  {cat.description.slice(0, 40)}...
                </p>
                <span className="mt-3 text-xs text-accent-600 font-medium">
                  {cat.articleCount} 篇文章
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                精选文章
              </h2>
              <p className="text-sm text-foreground-600">
                编辑团队精心挑选的优质内容
              </p>
            </div>
            <Link
              to="/category/nurse"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredArticles.map((article) => {
              const cat = categories.find((c) => c.slug === article.category);
              return (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full mb-2">
                      {cat?.name || ''}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <span>{article.date}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                最新发布
              </h2>
              <p className="text-sm text-foreground-600">
                持续更新的制服时尚内容
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {recentArticles.map((article) => {
              const cat = categories.find((c) => c.slug === article.category);
              return (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col sm:flex-row gap-4 bg-background-100 rounded-lg p-4 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="w-full sm:w-40 h-40 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="inline-block text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full mb-2">
                      {cat?.name || ''}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <span>{article.date}</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              关于制服时尚的常见疑问解答
            </p>
          </div>
          <div className="max-w-3xl mx-auto grid grid-cols-1 gap-4">
            {faqItems.slice(0, 6).map((item, index) => (
              <details key={index} className="group bg-background-50 rounded-lg p-5 cursor-pointer">
                <summary className="flex items-center justify-between gap-4 text-sm font-semibold text-foreground-950 list-none">
                  {item.question}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
            加入我们的社区
          </h2>
          <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
            订阅我们的资讯，第一时间获取最新的制服时尚内容和独家穿搭指南
          </p>
          <form
            data-readdy-form="true"
            action="https://readdy.ai/api/form/d9u4tklca23r0tr1aql0"
            method="POST"
            className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              placeholder="输入你的邮箱地址"
              required
              className="w-full px-4 py-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
            />
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-antispam"
            />
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              立即订阅
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}